// ชิ้นส่วนที่ใช้ร่วมกันของหน้าติดตามนักเรียนภาคศาสนา 3 หน้า
// (ระบบติดตามนักเรียน / เจาะรายคน / ติดตามการดูแลนักเรียน)
//
// ป้ายสีและตัวเลขเกณฑ์เป็นชุดเดียวกับภาคสามัญ (client/src/reports/*) เพื่อให้ครูที่ดู
// ทั้งสองภาคอ่านสีเดียวกันแล้วหมายความเหมือนกัน แต่ข้อความมาจากไฟล์ภาษา (th/jawi)
// เพราะเมนูภาคศาสนาสลับภาษาได้
import React, { useState } from 'react'
import { Badge, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Swal from 'sweetalert2'
import { useAuth } from '../services/authProvider'

export const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api')
  .replace(/\/api\/?$/, '').replace(/\/$/, '')

// ระดับ (ต่ำ → สูง) — ชุดเดียวกับ LEVEL_POINTS ใน islamicCareActionModel
export const LEVEL_POINTS = { green: 0, yellow: 1, orange: 2, red: 3 }
export const aboveWatch = (lv) => (LEVEL_POINTS[lv] || 0) > LEVEL_POINTS.yellow

// สีของแต่ละระดับ (ตรงกับ $danger/$orange/$warning/$success ของธีม)
export const RISK_BG = { red: 'danger', orange: 'orange', yellow: 'warning', green: 'success' }
export const TONE_HEX = { danger: '#dc3545', orange: '#fd7e14', warning: '#ffc107' }
export const tint = (hex, alpha) => `${hex}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`

export const riskLabel = (t, level) => t.risk[level] || t.risk.green

// ตำแหน่งฝั่งศาสนาที่เกี่ยวกับหน้าติดตาม (ตรงกับ server/utils/islamicLevels.js)
export const ISLAMIC_ADVISOR_POS = 51
export const ISLAMIC_ACADEMIC_HEAD_POS = 57
export const ISLAMIC_LEVEL_POS = [54, 55, 56, 58, 59]

// ป้ายบทบาทบน breadcrumb — ผู้ที่เห็นกว้างที่สุดชนะ (ตรงกับ attachIslamicTrackingScope)
export const useIslamicTrackingRole = (t) => {
  const { posIds } = useAuth()
  const has = (p) => Array.isArray(posIds) && posIds.includes(p)
  const roleLabel =
    (has(ISLAMIC_ACADEMIC_HEAD_POS) || has(99)) ? t.risk.roleAcademicHead
    : ISLAMIC_LEVEL_POS.some(has) ? t.risk.roleLevelHead
    : has(ISLAMIC_ADVISOR_POS) ? t.risk.roleAdvisor
    : t.risk.roleFallback
  return { posIds: posIds || [], roleLabel }
}

// ป้ายท้ายชื่อหน้าบอกว่าข้อมูลถูกจำกัดอยู่ระดับไหน เช่น " (ตอนกลาง)"
// levelKeys มาจาก server (/tracking/filterOptions) ชื่อระดับมาจากไฟล์ภาษา
export const levelSuffix = (t, levelKeys = []) => {
  const labels = (levelKeys || []).map(k => t.islamicLevel[k]).filter(Boolean)
  return labels.length ? ` (${labels.join(' + ')})` : ''
}

// รูปนักเรียน (hover ดูภาพใหญ่ + คลิกเปิด modal) — แพทเทิร์นเดียวกับหน้ารายงานภาคสามัญ
export const StudentAvatar = ({ photo, name, size = 32, zoom = true }) => {
  const [hovered, setHovered] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const initial = name ? name.trim().charAt(0) : '?'
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null
  const circleStyle = { width: size, height: size, borderRadius: '50%', flexShrink: 0 }
  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => zoom && src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={(e) => { if (zoom && src) { e.stopPropagation(); setShowModal(true) } }}
      >
        {src
          ? <img src={src} alt="" style={{ ...circleStyle, objectFit: 'cover', cursor: zoom ? 'pointer' : undefined }} />
          : <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.42, fontWeight: 600 }}>{initial}</span>}
        {zoom && src && hovered && (
          <div style={{ position: 'absolute', left: size + 6, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {zoom && src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            {name && <div className="mt-2 fw-semibold small">{name}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  )
}

// ป้ายระดับ — เหลือง/ส้มใช้ตัวอักษรสีเข้มเพื่อให้อ่านออกทั้งโหมดสว่างและมืด
export const LevelBadge = ({ level, children }) => {
  const bg = RISK_BG[level] || RISK_BG.green
  const darkText = bg === 'warning' || bg === 'orange'
  return <Badge bg={bg} text={darkText ? 'dark' : undefined} className="fs-xs">{children}</Badge>
}

export const InfoTip = ({ text }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
    <i className="ri-information-line ms-1 text-primary" style={{ cursor: 'help' }} />
  </OverlayTrigger>
)

// หน้าเหล่านี้ยิงคิวรีที่หนักที่สุดของระบบ ถ้าคนเปิดพร้อมกันเยอะจนคิวฝั่งเซิร์ฟเวอร์เต็ม
// จะได้ 503 กลับมา ซึ่งไม่ใช่ระบบพัง — บอกให้รอสักครู่แล้วกดลองใหม่ดีกว่าเด้ง
// "โหลดข้อมูลไม่สำเร็จ" ที่ครูทำอะไรต่อไม่ถูก (ฝั่ง server ดู utils/dbBusy.js)
export const alertRequestError = (t, err, fallbackTitle, { retry = false } = {}) => {
  const message = err?.response?.data?.message
  if (err?.response?.status === 503) {
    return Swal.fire({
      icon: 'info',
      title: t.careTracking.busyTitle,
      text: message || t.careTracking.busyText,
      showCancelButton: retry,
      confirmButtonText: retry ? t.careTracking.busyRetry : t.careTracking.busyUnderstood,
      cancelButtonText: t.careTracking.busyLater,
    })
  }
  return Swal.fire(fallbackTitle, message || t.careTracking.retryText, 'error')
}

// ตัวเลขที่ทำให้นักเรียนเข้าเกณฑ์ของแกนนั้น
export const axisDetail = (t, axisKey, a) => {
  if (!a) return ''
  if (axisKey === 'attendance') return t.careTracking.attendanceInfo(a)
  if (axisKey === 'behavior') return t.careTracking.behaviorInfo(a)
  return ''
}

export const fmtDate = (v) => {
  if (!v) return ''
  const d = new Date(v)
  return isNaN(d) ? '' : d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })
}

export const bmiOf = (h, w) => {
  const hh = Number(h || 0)
  const ww = Number(w || 0)
  return hh > 0 && ww > 0 ? +(ww / Math.pow(hh / 100, 2)).toFixed(1) : null
}

// เปิด/ปิด table-dark ตามธีม เหมือนหน้ารายงานอื่น (เรียกทุก render เพื่อให้ตารางที่
// mount หลังโหลดข้อมูลเสร็จได้สีถูกด้วย)
export const useDarkTables = () => {
  const dark = !!localStorage.getItem('skin-mode')
  React.useEffect(() => {
    for (const table of document.getElementsByTagName('table')) {
      table.querySelectorAll('thead').forEach(th =>
        dark ? th.classList.add('table-dark') : th.classList.remove('table-dark'))
    }
  })
}
